/**
 * Card-number / expiry / CVV utilities used by the deposit form.
 *
 * Pure functions, no React, no DOM — they exist as a separate module so
 * they can be unit-tested without spinning up the whole UI tree.
 */

export type CardBrand = 'visa' | 'mastercard' | 'amex' | 'discover' | 'unknown';

const BRAND_RULES: Array<{ brand: CardBrand; pattern: RegExp }> = [
  { brand: 'visa', pattern: /^4/ },
  { brand: 'mastercard', pattern: /^(5[1-5]|2[2-7])/ },
  { brand: 'amex', pattern: /^3[47]/ },
  { brand: 'discover', pattern: /^(6011|65|64[4-9])/ },
];

/**
 * Detect the card brand from a (possibly partial, possibly formatted) card
 * number. Returns `'unknown'` if no rule matches yet.
 */
export function detectCardBrand(value: string): CardBrand {
  const digits = value.replace(/\D/g, '');
  if (!digits) return 'unknown';
  for (const { brand, pattern } of BRAND_RULES) {
    if (pattern.test(digits)) return brand;
  }
  return 'unknown';
}

/**
 * Group digits according to the brand: Amex uses 4-6-5 (15 digits total),
 * everyone else uses 4-4-4-4 (16). Non-digit input is stripped.
 */
export function formatCardNumber(value: string, brand: CardBrand = detectCardBrand(value)): string {
  const digits = value.replace(/\D/g, '').slice(0, brand === 'amex' ? 15 : 16);
  if (!digits) return '';
  const groups = brand === 'amex' ? [4, 6, 5] : [4, 4, 4, 4];

  const out: string[] = [];
  let cursor = 0;
  for (const size of groups) {
    if (cursor >= digits.length) break;
    out.push(digits.slice(cursor, cursor + size));
    cursor += size;
  }
  return out.join(' ');
}

export function maxCardDigits(brand: CardBrand): number {
  return brand === 'amex' ? 15 : 16;
}

export function expectedCvvLength(brand: CardBrand): number {
  return brand === 'amex' ? 4 : 3;
}

/**
 * Format an MM/YY expiry. Accepts free-form digits and inserts the slash
 * once month has 2 digits. Caps at MMYY (4 digits → "MM/YY").
 */
export function formatExpiry(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
}

export interface ParsedExpiry {
  month: string;
  year: string;
  isValid: boolean;
}

/**
 * Parse "MM/YY" (or partial) into separate month / year strings, with a
 * validity flag based on month range and not-in-the-past against `now`.
 */
export function parseExpiry(value: string, now: Date = new Date()): ParsedExpiry {
  const digits = value.replace(/\D/g, '');
  const month = digits.slice(0, 2);
  const year = digits.slice(2, 4);
  if (month.length !== 2 || year.length !== 2) {
    return { month, year, isValid: false };
  }
  const monthNum = Number(month);
  if (!Number.isInteger(monthNum) || monthNum < 1 || monthNum > 12) {
    return { month, year, isValid: false };
  }
  const fullYear = 2000 + Number(year);
  const lastDayOfMonth = new Date(fullYear, monthNum, 0, 23, 59, 59, 999);
  return { month, year, isValid: lastDayOfMonth.getTime() >= now.getTime() };
}

/**
 * Luhn check. Used to surface obviously-typo'd card numbers before sending
 * the form to Pay.com (the SDK still does its own server-side validation).
 */
export function passesLuhn(cardNumber: string): boolean {
  const digits = cardNumber.replace(/\D/g, '');
  if (digits.length < 12) return false;
  let sum = 0;
  let shouldDouble = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    let digit = digits.charCodeAt(i) - 48;
    if (shouldDouble) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
    shouldDouble = !shouldDouble;
  }
  return sum % 10 === 0;
}
